import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  MapPin,
  Users,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Music,
  Coffee,
  Utensils,
  Flame,
  Shield,
  UserPlus,
  Compass,
  Star,
  Calendar,
} from 'lucide-react';
import './LandingPage.css';

/* ─── CTA Block ─────────────────────────────────────────────── */
/* The web app is retired - Barkada is iOS-only, so there is nothing to link to yet. */
const CTABlock: React.FC<{ size?: 'default' | 'large' }> = ({ size = 'default' }) => (
  <span className={`lp-btn lp-btn--primary${size === 'large' ? ' lp-btn--lg' : ''}`} aria-disabled="true">
    Coming soon to the App Store
  </span>
);

/* ─── Phone Mockup ───────────────────────────────────────────── */
const PhoneMockup: React.FC = () => (
  <div className="lp-phone">
    <div className="lp-phone__frame">
      <div className="lp-phone__notch" />
      <div className="lp-phone__screen">

        {/* Status bar */}
        <div className="lp-phone__statusbar">
          <span>9:41</span>
          <span className="lp-phone__statusbar-right">●●●</span>
        </div>

        {/* App bar */}
        <div className="lp-phone__appbar">
          <span className="lp-phone__app-logo">barkada</span>
        </div>

        {/* Activity Card */}
        <div className="lp-phone__card">
          <div className="lp-phone__card-img">
            <div className="lp-phone__card-img-overlay" />
            <div className="lp-phone__card-badge">
              <Music size={10} /> Tonight · 8 PM
            </div>
          </div>
          <div className="lp-phone__card-body">
            <div className="lp-phone__card-title">Concert Pregame</div>
            <div className="lp-phone__card-loc">
              <MapPin size={10} /> Warehouse District
            </div>
            <div className="lp-phone__avatars">
              {['A', 'J', 'M', 'R'].map((l, i) => (
                <div key={i} className={`lp-phone__avatar lp-phone__avatar--${i}`}>{l}</div>
              ))}
              <span className="lp-phone__going">+3 going</span>
            </div>
          </div>
        </div>

        {/* Match indicator */}
        <div className="lp-phone__match">
          <span className="lp-phone__match-dot" />
          <CheckCircle size={11} />
          Matched — You're In
        </div>

        {/* Squad vote */}
        <div className="lp-phone__squad">
          <div className="lp-phone__squad-label">
            <Users size={11} /> Squad Vote
          </div>
          <div className="lp-phone__squad-members">
            {[
              { name: 'You', vote: 'yes' },
              { name: 'Nia', vote: 'yes' },
              { name: 'Dev', vote: 'pending' },
            ].map((m) => (
              <div key={m.name} className={`lp-phone__member lp-phone__member--${m.vote}`}>
                <div className="lp-phone__member-av">{m.name[0]}</div>
                <span>{m.name}</span>
                <span className="lp-phone__member-status">{m.vote === 'yes' ? '✓' : '…'}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Action row
        <div className="lp-phone__actions">
          <button className="lp-phone__action-pass">✕</button>
          <button className="lp-phone__action-join">✓ Join</button>
        </div> */}

      </div>
    </div>
    <div className="lp-phone__glow" />
  </div>
);

/* ─── Squad Card Mockup ──────────────────────────────────────── */
const SquadCardMockup: React.FC = () => (
  <div className="lp-squad-card">
    <div className="lp-squad-card__top">
      <div>
        <div className="lp-squad-card__name">Friday Squad</div>
        <div className="lp-squad-card__sub">3 members voting</div>
      </div>
      <span className="lp-tag">Live</span>
    </div>

    <div className="lp-squad-card__activity">
      <div className="lp-squad-card__act-icon"><Utensils size={15} /></div>
      <div>
        <div className="lp-squad-card__act-title">Rooftop Dinner</div>
        <div className="lp-squad-card__act-meta">8 going · Midtown · 7:30 PM</div>
      </div>
    </div>

    <div className="lp-squad-card__votes">
      {[
        { init: 'Y', name: 'You', status: 'In', type: 'yes' },
        { init: 'N', name: 'Nia', status: 'In', type: 'yes' },
        { init: 'D', name: 'Dev', status: 'Thinking...', type: 'pending' },
      ].map((v) => (
        <div key={v.name} className={`lp-squad-card__vote lp-squad-card__vote--${v.type}`}>
          <div className="lp-squad-card__vote-av">{v.init}</div>
          <span className="lp-squad-card__vote-name">{v.name}</span>
          <span className="lp-squad-card__vote-status">{v.status}</span>
        </div>
      ))}
    </div>

    <div className="lp-squad-card__bar-wrap">
      <div className="lp-squad-card__bar-track">
        <div className="lp-squad-card__bar-fill" />
      </div>
      <span className="lp-squad-card__bar-label">2 of 3 ready</span>
    </div>
  </div>
);

/* ─── FAQ Item ───────────────────────────────────────────────── */
const FAQItem: React.FC<{ q: string; a: string }> = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className={`lp-faq-item${open ? ' lp-faq-item--open' : ''}`} onClick={() => setOpen(!open)}>
      <div className="lp-faq-item__q">
        <span>{q}</span>
        {open ? <ChevronUp size={17} /> : <ChevronDown size={17} />}
      </div>
      {open && <p className="lp-faq-item__a">{a}</p>}
    </div>
  );
};

/* ─── Main Page ──────────────────────────────────────────────── */
const LandingPage: React.FC = () => {

  return (
    <div className="landing-page">

      {/* ══ HERO ══════════════════════════════════════════════ */}
      <section className="landing-page__hero">
        <div className="landing-page__hero-orb landing-page__hero-orb--1" />
        <div className="landing-page__hero-orb landing-page__hero-orb--2" />
        <div className="landing-page__hero-orb landing-page__hero-orb--3" />

        <div className="landing-page__hero-content">
          {/* <span className="lp-pill">
            <Flame size={12} /> Early Access Open
          </span> */}

          <h1 className="landing-page__title">
            <br />
            <span className="text-primary"> Group Matchmaking for Activities</span>
          </h1>

          {/* <p className="landing-page__subtitle">
            The activity is happening. The right people are already going.
            You just haven't found each other yet.
          </p> */}

          <CTABlock />

          <p className="lp-hero__trust">
            <Shield size={12} className="lp-hero__trust-icon" />
            Not a dating app. No blind meetups. No pressure.
          </p>
        </div>

        <div className="landing-page__hero-image">
          <PhoneMockup />
        </div>
      </section>

      {/* ══ BARKADA ═══════════════════════════════════════════ */}
      <section className="lp-section landing-page__barkada">
        <div className="lp-container">
          <div className="lp-barkada__inner">
            <div className="lp-barkada__word">Barkada</div>
            <div className="lp-barkada__meta">
              <span>/bɑːrˈkɑːdə/</span>
              <span>·</span>
              <span>noun</span>
              <span>·</span>
              <span>origin: Filipino</span>
            </div>
            <div className="lp-barkada__divider" />
            <p className="lp-barkada__def">
              A close-knit group of friends — <em>the people you actually do things with.</em>
            </p>
          </div>
        </div>
      </section>

      {/* ══ PROBLEM ═══════════════════════════════════════════ */}
      <section className="lp-section landing-page__problem">
        <div className="lp-container">
          {/* <span className="lp-eyebrow">The Problem</span> */}
          <h2 className="lp-h2">
            Your circle is great.<br className="lp-br--desktop" /> It just doesn't cover everything.
          </h2>

          <div className="lp-problem__grid">
            {[
              {
                num: '01',
                text: 'You have a concert friend, a hiking friend, a food friend. Getting all the right people to the right plan at the same time is its own problem.',
              },
              {
                num: '02',
                text: "Your friends don't always share every interest. So either you skip the thing you want to do — or you go alone and hope for the best.",
              },
              {
                num: '03',
                text: "Going alone to something social isn't the hard part. Not knowing who you'll end up with — that's what keeps you home.",
              },
            ].map((item) => (
              <div className="lp-problem__item" key={item.num}>
                <span className="lp-problem__num">{item.num}</span>
                <p className="lp-problem__text">{item.text}</p>
              </div>
            ))}
          </div>

          {/* <blockquote className="lp-problem__quote">
            "You shouldn't have to choose between doing things you love and having people to do them with."
          </blockquote> */}
        </div>
      </section>

      {/* ══ PHILOSOPHY ════════════════════════════════════════ */}
      <section className="lp-section landing-page__philosophy">
        <div className="lp-container">
          <div className="lp-philosophy__layout">
            <div className="lp-philosophy__body">
              {/* <span className="lp-eyebrow">Our Belief</span> */}
              <h2 className="lp-h2 lp-h2--left">
                People bond through<br />what they do together.
              </h2>
              {/* <p className="lp-philosophy__body">
                Not by being introduced and hoping for chemistry. When two people care about
                the same thing enough to show up for it, the connection is already there.
                We just put you both in the same room.
              </p> */}
            </div>
            <div className="lp-philosophy__stats">
              {[
                { val: '0', label: 'blind meetups — you always know who you\'re joining before you confirm' },
                { val: '100%', label: 'mutual — both sides confirm before the plan is set' },
                { val: 'Your call', label: 'go solo, bring a friend, or move as a full squad — every time' },
              ].map((s) => (
                <div className="lp-philosophy__stat" key={s.val}>
                  <span className="lp-philosophy__stat-val">{s.val}</span>
                  <span className="lp-philosophy__stat-label">{s.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══ HOW IT WORKS ══════════════════════════════════════ */}
      <section className="lp-section landing-page__how">
        <div className="lp-container">
          {/* <span className="lp-eyebrow">The Process</span> */}
          <h2 className="lp-h2">How it works.</h2>

          <div className="lp-how__grid">
            {[
              {
                icon: <Compass size={22} />,
                num: '01',
                title: 'Discover',
                body: 'Find activities happening near you — concerts, hikes, dinners, game nights, and more.',
              },
              {
                icon: <Users size={22} />,
                num: '02',
                title: 'Preview',
                body: "See who's going, browse profiles, and get a feel for the group — before you commit to anything.",
              },
              {
                icon: <CheckCircle size={22} />,
                num: '03',
                title: 'Match',
                body: 'Join solo or bring your squad. Both sides confirm — then the plan is locked.',
              },
              {
                icon: <Star size={22} />,
                num: '04',
                title: 'Show Up',
                body: 'No expectations beyond the activity. Arrive confident. Leave with new stories.',
              },
            ].map((step, i) => (
              <div className="lp-how__step" key={i}>
                <div className="lp-how__step-top">
                  <span className="lp-how__step-num">{step.num}</span>
                  <div className="lp-how__step-icon">{step.icon}</div>
                </div>
                <h3 className="lp-how__step-title">{step.title}</h3>
                <p className="lp-how__step-body">{step.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* ══ HOST CONTROL / BROADCAST SECTION ════════════════════════════════════════ */}
      <section className="lp-section landing-page__host-control">
        <div className="lp-container">
          {/* <span className="lp-eyebrow">Post with Confidence</span> */}
          <h2 className="lp-h2">
            You don't have to broadcast your activity<br className="lp-br--desktop" /> to the whole city
          </h2>

          <p className="lp-host-control__body">
            Set simple preferences so it reaches people who would be interested.
          </p>
          {/* <p className="lp-host-control__body">
            Host a Galentines? Looking to try something new together? Want to play competitively?
            Just toggle it. No public exposure, no empty RSVPs, no awkward mismatches.
            Only the right interests show up privately, both sides confirm, and the plan comes together easily.
          </p> */}
          <div className="lp-host-control__cards">
            {/* Card 1: Gender restricted */}
            <div className="lp-activity-card lp-activity-card--gender">
              <div className="lp-activity-card__cover lp-activity-card__cover--hike" />
              <div className="lp-activity-card__body">
                <h4 className="lp-activity-card__title">Casual Morning Hike</h4>
                <div className="lp-activity-card__chips">
                  <span className="lp-chip lp-chip--gender">Women only</span>{" "}
                  <span className="lp-chip lp-chip--skill">Private</span>
                </div>
                <div className="lp-activity-card__meta">
                  <span>Sat, Mar 7 · 9:00 AM</span>
                  <span>Brunswick Trails · 4–6 spots</span>
                </div>
              </div>
            </div>

            {/* Card 2: Trying something new */}
            <div className="lp-activity-card lp-activity-card--intent">
              <div className="lp-activity-card__cover lp-activity-card__cover--pickleball" />
              <div className="lp-activity-card__body">
                <h4 className="lp-activity-card__title">First-Time Pickleball Session</h4>
                <div className="lp-activity-card__chips">
                  <span className="lp-chip lp-chip--intent">Trying something new</span>
                </div>
                <div className="lp-activity-card__meta">
                  <span>Sun, Mar 8 · 11:00 AM</span>
                  <span>Brunswick Courts · 4–8 people</span>
                </div>
              </div>
            </div>

            {/* Card 3: Skill level */}
            <div className="lp-activity-card lp-activity-card--skill">
              <div className="lp-activity-card__cover lp-activity-card__cover--games" />
              <div className="lp-activity-card__body">
                <h4 className="lp-activity-card__title">Board Game Night</h4>
                <div className="lp-activity-card__chips">
                  <span className="lp-chip lp-chip--skill">Intermediate</span>
                </div>
                <div className="lp-activity-card__meta">
                  <span>Fri, Mar 6 · 7:00 PM</span>
                  <span>Local Café · 4–6 players</span>
                </div>
              </div>
            </div>
          </div>

          {/* <div className="lp-host-control__captions">
            <span>Prefer women-only</span>
            <span>Open to trying something new</span>
            <span>Match on skill level</span>
          </div> */}
        </div>
      </section>
      {/* ══ SQUAD MODE ════════════════════════════════════════ */}
      <section className="lp-section landing-page__squad">
        <div className="lp-container">
          <div className="lp-squad__layout">
            <div className="lp-squad__text">
              {/* <span className="lp-pill lp-pill--outlined">Squad Mode</span> */}
              <h2 className="lp-h2 lp-h2--left">Bring your people.</h2>
              <p className="lp-squad__lead">
                Create a squad with your friends. Browse activities as a group.
                Vote collectively on what feels right before anyone commits.
              </p>
              {/* <ul className="lp-squad__list">
                {[
                  'Everyone sees the same activity cards',
                  'Group vote before anyone commits',
                  'Enter new spaces as a unit — not strangers',
                  'Comfort in numbers. Confidence in the plan.',
                ].map((item, i) => (
                  <li key={i} className="lp-squad__list-item">
                    <CheckCircle size={14} className="lp-squad__check" />
                    {item}
                  </li>
                ))}
              </ul> */}
            </div>

            <div className="lp-squad__visual">
              <SquadCardMockup />
            </div>
          </div>
        </div>
      </section>

      {/* ══ USE CASES ═════════════════════════════════════════ */}
      <section className="lp-section landing-page__use">
        <div className="lp-container">
          {/* <span className="lp-eyebrow">Who It's For</span> */}
          <h2 className="lp-h2">You'll recognize yourself in one of these.</h2>
          {/* <p className="lp-use__sub">
            Different situations, same underlying problem — the people around you
            don't always match where you want to go. Barkada fills that gap.
          </p> */}

          <div className="lp-use__chips">
            {[
              { icon: <Calendar size={15} />, label: 'Need a Friday night plan' },
              { icon: <MapPin size={15} />, label: 'New in town' },
              { icon: <UserPlus size={15} />, label: 'Expanding your circle' },
              { icon: <Star size={15} />, label: 'Looking for activity partners' },
              { icon: <Users size={15} />, label: 'Open to deeper friendships' },
              { icon: <Music size={15} />, label: 'Want a low-stakes night out' },
              { icon: <Coffee size={15} />, label: 'Just moved somewhere new' },
              { icon: <Flame size={15} />, label: 'Tired of the same routine' },
            ].map((item, i) => (
              <div className="lp-use__chip" key={i}>
                <span className="lp-use__chip-icon">{item.icon}</span>
                {item.label}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ STAKES ════════════════════════════════════════════ */}
      <section className="landing-page__stakes">
        <div className="lp-container">
          <h2 className="landing-page__stakes__h2">The alternative?</h2>
          <div className="landing-page__stakes__words">
            {['Scrolling.', 'Hesitating.', 'Staying in.', 'Wasted weekend.'].map((w, i) => (
              <span key={i} className="landing-page__stakes__word" style={{ animationDelay: `${i * 0.2}s` }}>
                {w}
              </span>
            ))}
          </div>
          <p className="landing-page__stakes__sub">You already know this feeling. We built the fix.</p>
        </div>
      </section>

      {/* ══ FINAL CTA ═════════════════════════════════════════ */}
      <section className="landing-page__final">
        <div className="lp-final__orb lp-final__orb--1" />
        <div className="lp-final__orb lp-final__orb--2" />
        <div className="lp-container landing-page__final__inner">
          <span className="lp-eyebrow lp-eyebrow--light">Be First</span>
          <h2 className="lp-final__h2">Be part of the first wave.</h2>
          <p className="lp-final__sub">
            The right people for the things you love are already out there.
            <br />Be among the first to find them.
          </p>
          <CTABlock size="large" />
          <p className="lp-final__trust">
            <Shield size={12} /> No spam. No pressure. Leave anytime.
          </p>
        </div>
      </section>

      {/* ══ FAQ ═══════════════════════════════════════════════ */}
      <section className="lp-section landing-page__faq">
        <div className="lp-container">
          <span className="lp-eyebrow">Questions</span>
          <h2 className="lp-h2">Good questions, clear answers.</h2>
          <div className="lp-faq__list">
            {[
              {
                q: 'Is this a dating app?',
                a: 'No. Link is a social activity platform. The focus is always on the activity — not romantic matching. You show up to do something, not to be evaluated.',
              },
              {
                q: 'Is it safe?',
                a: "All profiles are verified. Activities take place in public or semi-public settings. You always know who you're joining before you confirm — and mutual matching means no one can force their way into your plans.",
              },
              {
                q: 'Can I host an activity?',
                a: 'Yes. Anyone can create an activity listing. Set the details, decide who can join, and define the vibe you\'re going for. Your event, your terms.',
              },
              {
                q: 'Can I join as a squad?',
                a: "That's a core feature. Create a squad with friends, browse activities together, and vote as a group. You all join together — or not at all.",
              },
              {
                q: "What if I don't match with anyone?",
                a: "No match means no pressure. You keep browsing, revisit other activities, or host your own. There's no awkward rejection — just a quiet pass.",
              },
            ].map((item) => (
              <FAQItem key={item.q} q={item.q} a={item.a} />
            ))}
          </div>
        </div>
      </section>

      {/* ══ FOOTER ════════════════════════════════════════════ */}
      <footer className="lp-footer">
        <div className="lp-container lp-footer__inner">
          <span className="lp-footer__logo">barkada</span>
          <p className="lp-footer__copy">© 2025 Librio LLC. All rights reserved.</p>
        </div>
      </footer>

    </div>
  );
};

export default LandingPage;